// MACHINERY V2 (Stroje) — catalog category tabs with facet counts (M2.8).
//
// Spec: data package §11 — `category.id/code/name/modelCount` ("code=identity,
// modelCount=badge, name=fallback label") and the `query.categoryCode` filter
// row. Reference impl: components/CatalogCategoryTabs.tsx :22-37 (the two label
// maps). Artboards 0900 / 0901 draw the strip: a pill per category, the count
// in mono beside the label, "Všetko" first and selected.
//
// THE COUNT IS THE FACET, NOT THE RESULT COUNT. Each tab shows
// `category.modelCount` — how many models that category holds in TOTAL — and
// "All" shows their sum. It deliberately does NOT reflect the active rail
// filters: these counts come from `GET .../categories`, which takes no filter
// arguments, so making them filter-aware would need a backend change. v1 does
// the same (:sum over `categories`), and §11 states the rule outright ("category
// tab counts (+All=Σ)").
//
// The label falls back to the server's `name` for an unmapped code, per §13's
// "laser-machines appears in category-code UI maps — verify live set" caution.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { CatalogCategory } from "../../models/models";

/** Category code -> TAB label key. Distinct from the card badge's operation
 *  wording (catalogAtoms.tsx) — "Obrábacie centrá" vs "FRÉZOVANIE".
 *
 *  EXPORTED AT M2.16c. Below 1280 the category strip is rebuilt from the mobile
 *  kit (ChipScroller + TouchChip) rather than from the pills below, because this
 *  row WRAPS and is 32px tall — a page chip row must scroll with a right-edge
 *  fade (mobile/README.md §Standards) and must clear 44px. What both surfaces
 *  must agree on is the WORD, so the map is shared and the markup is not.
 *  See CatalogMobileChrome.tsx. */
export const CATALOG_TAB_LABEL_KEYS: Record<string, string> = {
  "machining-centers": "myMachinery:machiningCenters",
  "turning-machines": "myMachinery:turningMachines",
  "grinding-machines": "myMachinery:grindingMachines",
  edm: "myMachinery:edmMachines",
  "laser-machines": "myMachinery:laserMachines",
  "packaging-lines": "myMachinery:packagingLines",
};

export interface CatalogCategoryTabsV2Props {
  categories: CatalogCategory[];
  selectedCode?: string;
  onSelect: (code?: string) => void;
}

const Tab: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => {
  const { getCurrentLanguage } = useTranslation("myMachinery");
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        height: 32,
        padding: "0 13px",
        borderRadius: 999,
        cursor: "pointer",
        font: "inherit",
        fontSize: 12.5,
        fontWeight: active ? 600 : 500,
        whiteSpace: "nowrap",
        flexShrink: 0,
        background: active ? "var(--mod-accent)" : "var(--card)",
        border: "1px solid " + (active ? "var(--mod-accent)" : "var(--border)"),
        color: active ? "var(--mod-accent-fg)" : "var(--ink-2)",
      }}
    >
      <span>{label}</span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          opacity: active ? 0.85 : 1,
          color: active ? "var(--mod-accent-fg)" : "var(--faint)",
        }}
      >
        {count.toLocaleString(getCurrentLanguage())}
      </span>
    </button>
  );
};

const CatalogCategoryTabsV2: React.FC<CatalogCategoryTabsV2Props> = ({
  categories,
  selectedCode,
  onSelect,
}) => {
  const { translate } = useTranslation("myMachinery");
  const total = categories.reduce((sum, c) => sum + c.modelCount, 0);

  return (
    <div
      role="group"
      aria-label={translate("myMachinery:filterBy")}
      style={{ display: "flex", gap: 8, flexWrap: "wrap", minWidth: 0 }}
    >
      <Tab
        label={translate("myMachinery:allCategories")}
        count={total}
        active={!selectedCode}
        onClick={() => onSelect(undefined)}
      />
      {categories.map((cat) => {
        const key = CATALOG_TAB_LABEL_KEYS[cat.code];
        return (
          <Tab
            key={cat.code}
            label={key ? translate(key) : cat.name}
            count={cat.modelCount}
            active={selectedCode === cat.code}
            onClick={() => onSelect(cat.code)}
          />
        );
      })}
    </div>
  );
};

export default CatalogCategoryTabsV2;
